import React, { useEffect, useState, useCallback } from "react";
import { Card } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";

import { fetchTicketsCreatedClosed } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

const PURPLE = "#6f42c1";
const BRIGHT_GREEN = "#22c55e";

const formatClosureVsIntake = (pct) => {
  if (pct == null) return "-";
  return `${pct}%`;
};

// Each period gets its OWN max so bars are always visible regardless of scale difference
const BarRow = ({ label, created, closed, resolvedPct }) => {
  const [showDeltaHint, setShowDeltaHint] = useState(false);
  const rowMax = Math.max(created, closed, 1);
  const createdPct = (created / rowMax) * 100;
  const closedPct = (closed / rowMax) * 100;
  const delta = closed - created;
  const absDelta = Math.abs(delta);
  const ticketWord = absDelta === 1 ? "ticket" : "tickets";

  let deltaHint = "= Intake matched closures";
  if (delta < 0) {
    deltaHint = `▲ ${absDelta} ${ticketWord} still open from ${label.toLowerCase()}`;
  } else if (delta > 0) {
    deltaHint = `✓ Cleared backlog by ${absDelta} ${ticketWord}`;
  }

  return (
    <div
      style={{ marginBottom: 20 }}
      onMouseEnter={() => setShowDeltaHint(true)}
      onMouseLeave={() => setShowDeltaHint(false)}
    >
      {/* Period label */}
      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--color-text)", marginBottom: 8 }}>
        {label}
      </div>

      {/* Created bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <div style={{ width: 70, fontSize: 12, color: PURPLE, fontWeight: 600, textAlign: "right", flexShrink: 0 }}>
          Created
        </div>
        <div style={{ flex: 1, background: "rgba(111,66,193,0.08)", borderRadius: 6, height: 22, overflow: "hidden" }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${createdPct}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{
              height: "100%",
              background: `${PURPLE}dd`,
              borderRadius: 6,
              minWidth: created > 0 ? 6 : 0,
            }}
          />
        </div>
        <div style={{ width: 170, fontSize: 13, fontWeight: 700, color: PURPLE, flexShrink: 0, textAlign: "left" }}>
          <span>{created}</span>
        </div>
      </div>

      {/* Closed bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 70, fontSize: 12, color: BRIGHT_GREEN, fontWeight: 600, textAlign: "right", flexShrink: 0 }}>
          Closed
        </div>
        <div style={{ flex: 1, background: "rgba(34,197,94,0.08)", borderRadius: 6, height: 22, overflow: "hidden" }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${closedPct}%` }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            style={{
              height: "100%",
              background: `${BRIGHT_GREEN}dd`,
              borderRadius: 6,
              minWidth: closed > 0 ? 6 : 0,
            }}
          />
        </div>
        <div style={{ width: 170, flexShrink: 0, textAlign: "left" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: BRIGHT_GREEN }}>{closed}</span>
          {resolvedPct != null && (
            <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600, marginLeft: 6 }}>
              {formatClosureVsIntake(resolvedPct)} closure vs intake
            </span>
          )}
          <div
            style={{
              marginTop: 3,
              fontSize: 11,
              color: "var(--color-text-light)",
              whiteSpace: "nowrap",
              opacity: showDeltaHint ? 1 : 0,
              transition: "opacity 0.18s ease",
              pointerEvents: "none",
              lineHeight: 1.2,
            }}
          >
            {deltaHint}
          </div>
        </div>
      </div>
    </div>
  );
};

const CreatedClosedChart = ({ clientId }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    const cacheKey = `tickets_created_closed_v2_${clientId}`;
    const cached = getCachedData(cacheKey, clientId);
    if (cached && cached.last_24h !== undefined) {
      setData(cached);
      return;
    }
    setLoading(true);
    try {
      const result = await fetchTicketsCreatedClosed(clientId);
      setData(result);
      setCachedData(cacheKey, result, clientId);
    } catch (err) {
      console.error("CreatedClosedChart fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId, getCachedData, setCachedData]);

  useEffect(() => {
    setData(null);
    fetchData();
  }, [clientId, fetchData]);

  const periods = [
    { label: "Last 24h",     key: "last_24h" },
    { label: "Last 7 Days",  key: "last_7d" },
    { label: "Last 30 Days", key: "last_30d" },
  ];

  return (
    <Card
      style={{
        borderRadius: 16,
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        minHeight: "calc(50dvh + 185px)",
        height: "100%",
      }}
    >
      <Card.Body style={{ padding: "20px 24px", display: "flex", flexDirection: "column", height: "100%" }}>

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            <div style={{ background: "rgba(111,66,193,0.1)", borderRadius: "8px", padding: "8px" }}>
              <FaCheckCircle size={18} color={PURPLE} />
            </div>
            <div>
              <h6 className="fw-bold m-0" style={{ color: "var(--color-text)" }}>
                Created vs. Closed
              </h6>
              <small style={{ opacity: 0.7, color: "var(--color-text-light)", display: "block", marginTop: 2 }}>
                Work completed vs incoming volume
              </small>
            </div>
          </div>

          <div className="d-flex gap-3">
            {[{ c: PURPLE, l: "Created" }, { c: BRIGHT_GREEN, l: "Closed" }].map(item => (
              <div key={item.l} className="d-flex align-items-center gap-2">
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.c }} />
                <span style={{ fontSize: 12, color: "var(--color-text-light)", fontWeight: 600 }}>{item.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Keep a small breathing gap under subtitle without pushing rows too far down */}
        <div style={{ height: 20, flexShrink: 0 }} />

        {/* Body */}
        <div style={{ minHeight: "50dvh", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
          {loading || !data ? (
            <div className="h-100 d-flex align-items-center justify-content-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                style={{
                  width: 24,
                  height: 24,
                  border: `3px solid ${PURPLE}22`,
                  borderTopColor: PURPLE,
                  borderRadius: "50%",
                }}
              />
            </div>
          ) : (
            periods.map(p => (
              <BarRow
                key={p.key}
                label={p.label}
                created={data?.[p.key]?.created ?? 0}
                closed={data?.[p.key]?.closed ?? 0}
                resolvedPct={data?.[p.key]?.resolved_pct ?? null}
              />
            ))
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-light)", opacity: 0.9, marginTop: 6 }}>
          Closure vs intake can exceed 100% when older backlog tickets are closed in this period.
        </div>
      </Card.Body>
    </Card>
  );
};

export default CreatedClosedChart;